import {
  DANGEROUS_PERMISSIONS,
  type DeviceGroup,
  type DeviceListRow,
  type Permission,
  type Role,
  type RoleAssignment,
  type RoleAssignmentScope,
  type RoleAssignmentScopeInput,
  type Site,
  type Uuid,
} from '@/lib/api/types';

/*
  ============================================================================
  The scope model from docs/07-identity-rbac.md I.2, computed in the browser.
  ============================================================================

  READ THIS FIRST. Everything in this file is an EXPLANATION, never a control.

  I.3 is explicit: the UI is never consulted, and a hand-crafted request with the same token
  gets exactly the same answer from the API. So the purpose of computing any of this on the
  client is to be able to tell the user what will happen, and why something is refused, before
  they spend a round trip finding out. If this file disagreed with the server, the server would
  still be right and the only consequence would be a confusing message. That is the correct
  failure mode, and it is why none of these functions is named "authorize".

  The model itself, in full, because it is small enough to state:

      tenant
        └── organization
              └── site
                    └── device_group
                          └── device

    * An assignment is a (user, role, scope) triple, where scope is one node or none, and none
      means tenant-wide.
    * An assignment at a node grants its role's permissions at that node AND EVERYWHERE BELOW.
    * There are no deny rules, no priority ordering, no inheritance overrides. Removing access
      means removing or narrowing an assignment.
    * An expired assignment grants nothing.

  Two consequences that shape the UI:

    1. The effective permissions of a role AT a scope are simply the role's permissions. What
       changes with the scope is the SET OF MACHINES they apply to, not which permissions they
       are. The preview therefore shows the permission list and, separately and prominently,
       what the scope reaches. Showing only a permission list would answer half the question.

    2. The no-escalation rule: a user may not grant a permission they do not themselves hold AT
       THAT SCOPE. The scope part matters. Someone with device.reboot on one organization cannot
       use it to grant device.reboot tenant-wide, because tenant is above their node and their
       own assignment says nothing there. So the check is not a set comparison against a flat
       list of the grantor's permissions; it walks the ancestor chain of the target scope.

  A NOTE ON CurrentUser.permissions. That field is the signed-in user's permissions flattened
  across every assignment, with the scopes discarded. It is the right input for "should this
  button exist", which is all AuthProvider.can() is for. It is the WRONG input for the
  escalation check, because flattening loses exactly the information the rule turns on. So the
  functions below take the grantor's assignments, not their flattened permissions.
*/

/* ------------------------------------------------------------------ nodes */

/**
 * A node in the hierarchy, as a comparable string. Scope nodes are compared by identity often
 * enough that a key beats structural comparison, and the kind prefix keeps a site id and a
 * group id that happen to collide from ever being treated as the same node.
 */
export type ScopeKey = string;

export function scopeKey(scope: RoleAssignmentScope | RoleAssignmentScopeInput): ScopeKey {
  switch (scope.kind) {
    case 'tenant':
      return 'tenant';
    case 'organization':
      return `organization:${scope.organizationId}`;
    case 'site':
      return `site:${scope.siteId}`;
    case 'device_group':
      return `device_group:${scope.deviceGroupId}`;
    case 'device':
      return `device:${scope.deviceId}`;
  }
}

/** The hierarchy data needed to walk a chain. Passed in rather than fetched here. */
export interface HierarchySnapshot {
  readonly sites: readonly Site[];
  readonly groups: readonly DeviceGroup[];
  readonly devices: readonly DeviceListRow[];
}

/**
 * Every node from `scope` up to the tenant, inclusive of both ends.
 *
 * This is the list of places an assignment could sit and still cover `scope`. A device's chain
 * skips the group or the site when the device is not in one, which is correct: a device with no
 * group is not reachable by a group-scoped assignment, and there is no group to name.
 *
 * A node whose parent cannot be resolved from the snapshot stops the walk early rather than
 * guessing. That produces a narrower chain, so the worst case is telling the user they cannot
 * grant something they actually can, which is the safe direction for an advisory check.
 */
export function ancestorChain(
  scope: RoleAssignmentScopeInput,
  hierarchy: HierarchySnapshot,
): readonly ScopeKey[] {
  const chain: ScopeKey[] = [scopeKey(scope)];

  const addOrganization = (organizationId: Uuid): void => {
    chain.push(`organization:${organizationId}`);
  };

  switch (scope.kind) {
    case 'tenant':
      return chain;

    case 'organization':
      chain.push('tenant');
      return chain;

    case 'site': {
      const site = hierarchy.sites.find((candidate) => candidate.id === scope.siteId);
      if (site !== undefined) {
        addOrganization(site.organizationId);
      }
      chain.push('tenant');
      return chain;
    }

    case 'device_group': {
      const group = hierarchy.groups.find((candidate) => candidate.id === scope.deviceGroupId);
      if (group !== undefined) {
        if (group.siteId !== null) {
          chain.push(`site:${group.siteId}`);
        }
        addOrganization(group.organizationId);
      }
      chain.push('tenant');
      return chain;
    }

    case 'device': {
      const row = hierarchy.devices.find((candidate) => candidate.device.id === scope.deviceId);
      if (row !== undefined) {
        if (row.device.deviceGroupId !== null) {
          chain.push(`device_group:${row.device.deviceGroupId}`);
        }
        if (row.device.siteId !== null) {
          chain.push(`site:${row.device.siteId}`);
        }
        addOrganization(row.device.organizationId);
      }
      chain.push('tenant');
      return chain;
    }
  }
}

/* ----------------------------------------------------------- what is held */

function isLive(assignment: RoleAssignment, now: number): boolean {
  return assignment.expiresAt === null || Date.parse(assignment.expiresAt) > now;
}

/**
 * The permissions a user holds at a given scope, from their own assignments.
 *
 * An assignment counts when its node is on the target scope's ancestor chain: at the node
 * itself, or anywhere above it. An assignment strictly BELOW the target does not count, which
 * is the whole point of the rule.
 */
export function permissionsHeldAt(
  assignments: readonly RoleAssignment[],
  roles: readonly Role[],
  scope: RoleAssignmentScopeInput,
  hierarchy: HierarchySnapshot,
  now: number = Date.now(),
): ReadonlySet<Permission> {
  const covering = new Set(ancestorChain(scope, hierarchy));
  const held = new Set<Permission>();

  for (const assignment of assignments) {
    if (!isLive(assignment, now)) {
      continue;
    }
    if (!covering.has(scopeKey(assignment.scope))) {
      continue;
    }
    const role = roles.find((candidate) => candidate.id === assignment.roleId);
    if (role === undefined) {
      continue;
    }
    for (const permission of role.permissions) {
      held.add(permission);
    }
  }

  return held;
}

/* ------------------------------------------------------------- the preview */

export interface PermissionPreviewEntry {
  readonly permission: Permission;
  readonly dangerous: boolean;
  /** False when the grantor does not hold this permission at the chosen scope. */
  readonly grantable: boolean;
}

export interface GrantPreview {
  readonly role: Role;
  readonly scope: RoleAssignmentScopeInput;
  /** Every permission the role bundles, marked for danger and for grantability. */
  readonly entries: readonly PermissionPreviewEntry[];
  /** The subset the grantor cannot pass on. Non-empty means the grant must be blocked. */
  readonly blocked: readonly Permission[];
  readonly dangerousCount: number;
}

/**
 * What granting `role` at `scope` would mean, and whether this grantor may do it.
 *
 * `blocked` being non-empty is the refusal. The caller disables the confirm button and shows
 * the list, rather than letting the request go and rendering a 403 that names permissions the
 * user never saw. The server independently refuses the same thing; the mock transport
 * reproduces that refusal on purpose so the two paths are both exercised.
 */
export function previewGrant(
  role: Role,
  scope: RoleAssignmentScopeInput,
  grantorAssignments: readonly RoleAssignment[],
  roles: readonly Role[],
  hierarchy: HierarchySnapshot,
): GrantPreview {
  const held = permissionsHeldAt(grantorAssignments, roles, scope, hierarchy);

  const entries = role.permissions.map<PermissionPreviewEntry>((permission) => ({
    permission,
    dangerous: DANGEROUS_PERMISSIONS.includes(permission),
    grantable: held.has(permission),
  }));

  return {
    role,
    scope,
    entries,
    blocked: entries.filter((entry) => !entry.grantable).map((entry) => entry.permission),
    dangerousCount: entries.filter((entry) => entry.dangerous).length,
  };
}

/* ------------------------------------------------------------ scope labels */

/** Human label for a scope, including the part above it, so it is unambiguous in a list. */
export function describeScope(
  scope: RoleAssignmentScopeInput,
  hierarchy: HierarchySnapshot,
  organizationNames: ReadonlyMap<Uuid, string>,
): string {
  switch (scope.kind) {
    case 'tenant':
      return 'The whole tenant';
    case 'organization':
      return organizationNames.get(scope.organizationId) ?? 'Unknown organization';
    case 'site': {
      const site = hierarchy.sites.find((candidate) => candidate.id === scope.siteId);
      if (site === undefined) {
        return 'Unknown site';
      }
      return `${organizationNames.get(site.organizationId) ?? 'Unknown'} / ${site.name}`;
    }
    case 'device_group': {
      const group = hierarchy.groups.find((candidate) => candidate.id === scope.deviceGroupId);
      if (group === undefined) {
        return 'Unknown device group';
      }
      return `${organizationNames.get(group.organizationId) ?? 'Unknown'} / ${group.name}`;
    }
    case 'device': {
      const row = hierarchy.devices.find((candidate) => candidate.device.id === scope.deviceId);
      return row === undefined ? 'Unknown device' : `${row.organizationName} / ${row.device.hostname}`;
    }
  }
}

/**
 * How many enrolled devices a scope actually reaches.
 *
 * This is the number that makes a scope choice concrete. "Technician on Contoso Freight" and
 * "Technician on Contoso Freight / Dispatch Terminals" are the same permission list and a very
 * different amount of access, and a count is the shortest way to say so.
 *
 * Counted from the devices this account can already see, so it is a lower bound rather than a
 * guarantee: a device the grantor cannot list is not counted. The UI says as much.
 */
export function devicesInScope(
  scope: RoleAssignmentScopeInput,
  hierarchy: HierarchySnapshot,
): readonly DeviceListRow[] {
  switch (scope.kind) {
    case 'tenant':
      return hierarchy.devices;
    case 'organization':
      return hierarchy.devices.filter(
        (row) => row.device.organizationId === scope.organizationId,
      );
    case 'site':
      return hierarchy.devices.filter((row) => row.device.siteId === scope.siteId);
    case 'device_group':
      return hierarchy.devices.filter(
        (row) => row.device.deviceGroupId === scope.deviceGroupId,
      );
    case 'device':
      return hierarchy.devices.filter((row) => row.device.id === scope.deviceId);
  }
}
